<template>
    <div class="ci-wrap">
        <!-- View Mode -->
        <div class="ci" v-if="comment && !editMode">
            <UserIcon />
            <div class="ci__body">
                <div class="ci__header">
                    <div class="ci__meta">
                        <span class="ci__author">
                            {{ comment.User.firstName }}
                        </span>
                        <span class="ci__role-badge" v-if="userTypeLabel">{{ userTypeLabel }}</span>
                        <span class="ci__date">
                            <i class="far fa-clock"></i>
                            {{ new Date(comment.createdAt).toDateString() }}
                        </span>
                    </div>
                    <div class="ci__controls" v-if="comment.User.id == this.user.id">
                        <button class="ci__ctrl-btn" @click="handleEditMode" title="Edit">
                            <i class="fas fa-pen"></i>
                        </button>
                        <button
                            class="ci__ctrl-btn ci__ctrl-btn--danger"
                            @click="deleteComment"
                            title="Delete"
                            :disabled="deleting"
                        >
                            <i class="fas fa-spinner fa-spin" v-if="deleting"></i>
                            <i class="fas fa-trash-alt" v-else></i>
                        </button>
                    </div>
                </div>
                <div class="ci__content" v-html="comment.description" />
                <div class="ci__footer" v-if="comment.attachment1 || comment.attachment2 || comment.attachment3 || (comment.taggedUsers && comment.taggedUsers.length)">
                    <div class="ci__attachments" v-if="comment.attachment1 || comment.attachment2 || comment.attachment3">
                        <AttachmentUploader
                            @remove="addToRemoveList('attachment1')"
                            v-if="comment.attachment1"
                            v-model="comment.attachment1"
                            readonly
                            :id="1"
                            :name="comment.attachment1"
                        />
                        <AttachmentUploader
                            @remove="addToRemoveList('attachment2')"
                            v-if="comment.attachment2"
                            v-model="comment.attachment2"
                            readonly
                            :id="2"
                            :name="comment.attachment2"
                        />
                        <AttachmentUploader
                            @remove="addToRemoveList('attachment3')"
                            v-if="comment.attachment3"
                            v-model="comment.attachment3"
                            readonly
                            :id="3"
                            :name="comment.attachment3"
                        />
                    </div>
                    <TagUser :viewMode="true" :taggedUsers="comment.taggedUsers || []" />
                </div>
            </div>
        </div>

        <!-- Edit Mode -->
        <div v-if="editMode" class="ci-edit">
            <CommentForm
                @updated="handleUpdated"
                @cancel="editMode = false"
                :commentData="comment"
                :taskId="taskId"
            />
        </div>
    </div>
</template>

<script>
import UserIcon from "./UserIcon.vue"
import AttachmentUploader from "@/components/other/AttachmentUploader"
import CommentForm from "@/components/Lists/CommentForm"
import TagUser from "@/components/other/TagUser"
import { mapState } from "vuex"
import { http } from "@/utils/http"

export default {
    props: {
        comment: {
            type: Object,
            default: () => null,
        },
        taskId: {
            type: String,
            type: [String, Number],
            default: () => "",
        },
        task: { type: Object, default: null },
    },
    data() {
        return {
            editMode: false,
            deleting: false,
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        userTypeLabel() {
            if (this.task.reporterId && String(this.task.reporterId) == String(this.comment.User)) {
                return "Creator"
            } else if (
                this.task.assigneeUserIds &&
                this.task.assigneeUserIds
                    .map((item) => item.id)
                    .includes(this.comment.User && this.comment.User.id)
            ) {
                return "Assignee"
            }
            return ""
        },
    },
    components: { UserIcon, AttachmentUploader, CommentForm, TagUser },
    methods: {
        handleUpdated(val) {
            this.$emit("update", val)
            this.editMode = false
        },
        async deleteComment() {
            if (this.deleting) return
            if (confirm("Are you sure you want to delete this comment?")) {
                this.deleting = true
                try {
                    await http.delete("/project-comments/" + this.comment.id)
                    this.$toast.info("Comment Deleted")
                    this.$emit("remove", this.comment)
                } catch (err) {
                    console.log("error in deleting comment", err)
                    this.$toast.error("Some error occured")
                }
            }
            this.deleting = false
        },
        handleEditMode() {
            this.editMode = true
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.ci-wrap {
    border-bottom: 1px solid $gray100;
    &:last-child { border-bottom: none; }
}

.ci {
    display: flex;
    gap: 10px;
    padding: 14px 18px;
    align-items: flex-start;
    transition: background $transitionFast;

    &:hover { background: rgba($gray50, 0.5); }

    &__body {
        flex: 1;
        min-width: 0;
    }
    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 6px;
        gap: 8px;
    }
    &__meta {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }
    &__author {
        font-size: 13px;
        font-weight: 700;
        color: $gray800;
    }
    &__role-badge {
        display: inline-flex;
        align-items: center;
        padding: 1px 8px;
        border-radius: 10px;
        font-size: 10px;
        font-weight: 600;
        background: $primaryLight;
        color: $primaryColor;
        text-transform: uppercase;
        letter-spacing: 0.3px;
    }
    &__date {
        font-size: 11px;
        color: $gray400;
        display: flex;
        align-items: center;
        gap: 4px;
        i { font-size: 10px; }
    }
    &__controls {
        display: flex;
        align-items: center;
        gap: 4px;
        opacity: 0;
        transition: opacity $transitionFast;
    }
    &:hover &__controls {
        opacity: 1;
    }
    &__ctrl-btn {
        width: 28px;
        height: 28px;
        border-radius: 6px;
        border: 1px solid transparent;
        background: transparent;
        color: $gray400;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        font-size: 11px;
        transition: all $transitionFast;

        &:hover {
            background: $gray100;
            color: $gray700;
            border-color: $gray200;
        }
        &--danger:hover {
            background: lighten($danger, 38%);
            color: $danger;
            border-color: lighten($danger, 28%);
        }
        &:disabled { opacity: 0.5; cursor: not-allowed; }
    }
    &__content {
        font-size: 13px;
        line-height: 1.6;
        color: $gray700;
        word-break: break-word;

        ::v-deep {
            p { margin: 0 0 6px; }
            p:last-child { margin-bottom: 0; }
            a { color: $primaryColor; }
            strong { color: $gray800; }
        }
    }
    &__footer {
        margin-top: 10px;
    }
    &__attachments {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        margin-bottom: 6px;
    }
}

.ci-edit {
    padding: 14px 18px;
}
</style>
